import { Eye, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useUser } from '@/contexts/UserContext'
import { GRACKLE_COLORS } from '@/lib/theme'

/**
 * ImpersonationBanner - Displays a prominent banner when admin is impersonating a user
 * WCAG 2.2 AA compliant with proper ARIA attributes
 */
export function ImpersonationBanner() {
  const { impersonatedUser, endImpersonation, isImpersonating } = useUser()

  if (!isImpersonating || !impersonatedUser) {
    return null
  }

  const handleExitImpersonation = () => {
    endImpersonation()
    // Optionally reload to refresh data in the actual admin context
    window.location.reload()
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-50 shadow-md"
      style={{ backgroundColor: GRACKLE_COLORS.darkRaspberry, color: '#ffffff' }} // brand-ok
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5" aria-hidden="true" />
            <span className="font-medium">
              Viewing as <strong className="font-bold">{impersonatedUser.name}</strong>
              <span className="hidden sm:inline"> ({impersonatedUser.email})</span>
              <span
                className="ml-2 px-2 py-0.5 text-xs rounded-full font-semibold"
                style={{ backgroundColor: GRACKLE_COLORS.prussianBlue, color: '#ffffff' }} // brand-ok
              >
                Read-only mode
              </span>
            </span>
          </div>
          <Button
            variant="ghost"
            size="default"
            onClick={handleExitImpersonation}
            className="min-h-[44px] min-w-[44px] focus-visible:ring-2 focus-visible:ring-offset-2"
            style={
              {
                color: '#ffffff', // brand-ok
                '--tw-ring-offset-color': GRACKLE_COLORS.darkRaspberry,
              } as React.CSSProperties
            }
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = GRACKLE_COLORS.prussianBlue
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
            aria-label="Exit impersonation mode and return to your admin view"
          >
            <X className="h-4 w-4 mr-1" aria-hidden="true" />
            <span className="hidden sm:inline">Exit Impersonation</span>
            <span className="sm:hidden">Exit</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
